import type { TextDirection } from '../views/LabelBuilder/LabelBuilder.slice'

interface TextLayoutArgs {
  text: string
  maxWidth?: number | null
  maxHeight?: number | null
  fontSize: number
  fontFamily: string
  fontStyle?: string
  direction?: TextDirection
}

interface TextLayoutResult {
  displayText: string
  lines: string[]
  lineHeight: number
  width?: number
  height: number
}

function createMeasurementContext (fontSize: number, fontFamily: string, fontStyle = 'normal') {
  if (typeof document === 'undefined') return null

  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) return null

  context.font = `${fontStyle} ${fontSize}px ${fontFamily}`
  return context
}

function ellipsizeLine (line: string, maxWidth: number, context: CanvasRenderingContext2D) {
  if (!maxWidth || maxWidth <= 0) return line
  if (context.measureText(line).width <= maxWidth) return line

  const ellipsis = '...'
  if (context.measureText(ellipsis).width > maxWidth) return ''

  let truncated = line
  while (truncated.length > 0 && context.measureText(`${truncated}${ellipsis}`).width > maxWidth) {
    truncated = truncated.slice(0, -1)
  }

  return `${truncated}${ellipsis}`
}

function applyTextDirection (text: string, direction: TextDirection = 'ltr') {
  if (direction === 'ttb') {
    return Array.from(text).join('\n')
  }

  if (direction === 'rtl') {
    return text
      .split('\n')
      .map(line => Array.from(line).reverse().join(''))
      .join('\n')
  }

  return text
}

export function getTextLayout ({
  text,
  maxWidth,
  maxHeight,
  fontSize,
  fontFamily,
  fontStyle = 'normal',
  direction = 'ltr'
}: TextLayoutArgs): TextLayoutResult {
  const rawLines = applyTextDirection(String(text || ''), direction).split('\n')
  const lineHeight = fontSize * 1.2
  const visibleLineCount = maxHeight && maxHeight > 0
    ? Math.max(0, Math.floor(maxHeight / lineHeight))
    : rawLines.length

  const visibleLines = rawLines.slice(0, visibleLineCount)
  const context = createMeasurementContext(fontSize, fontFamily, fontStyle)

  const lines = context && maxWidth && maxWidth > 0
    ? visibleLines.map(line => ellipsizeLine(line, maxWidth, context))
    : visibleLines

  return {
    displayText: lines.join('\n'),
    lines,
    lineHeight,
    width: maxWidth && maxWidth > 0 ? maxWidth : undefined,
    height: maxHeight && maxHeight > 0 ? maxHeight : lines.length * lineHeight
  }
}
